import { Bar } from "react-chartjs-2";
import { useMemo } from "react";
import { key_under_education } from "../../education/functions/options";
import { cn } from "../../../utils/useFunctions";

const allChartColors = [
  "#47A3F7", // income_peryear
  "#00B61D", // remain_accumulate
  "#F7A647", // outcome_peryear
  "#ff33b2", // out_come_stable
  "#FF0000", // expenese_car_items, expenese_children_items, expenese_collection_items, expenese_other_items
  "#00BDCE", // income_legacy_items
  "#00FFFF", // income_business_items
  "#FFA500", // income_property_items
  "#0000FF", // income_other_items
  "#0bdaf2", // pension_peryear
  "#CCCCCC", // fallback
];
// Generate a random hot tone color (red, orange, yellow range) that is not in existingColors
const getRandomHotHexColor = (existingColors = []) => {
  let color;
  let attempts = 0;
  do {
    const hue = Math.floor(Math.random() * 60); // 0-59 (red to yellow)
    const saturation = Math.floor(Math.random() * 21) + 80; // 80-100%
    const lightness = Math.floor(Math.random() * 16) + 45; // 45-60%
    color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    attempts++;
    if (attempts > 20) break; // avoid infinite loop
  } while (allChartColors.includes(color));
  return color;
};

// Generate a random cold tone color (blue, cyan, green range) that is not in existingColors
const getRandomColdHexColor = (existingColors = []) => {
  let color;
  let attempts = 0;
  do {
    const hue = Math.floor(Math.random() * 100) + 160; // 160-259
    const saturation = Math.floor(Math.random() * 31) + 70; // 70-100%
    const lightness = Math.floor(Math.random() * 21) + 45; // 45-65%
    color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    attempts++;
    if (attempts > 20) break;
  } while (allChartColors.includes(color));
  return color;
};

const coldToneColors = [
  "#006666",
  "#006677",
  "#006688",
  "#006699",
  "#007777",
  "#007788",
  "#007799",
  "#0077aa",
  "#0077bb",
  "#008888",
  "#008899",
  "#0088aa",
  "#0088bb",
  "#0088cc",
  "#009988",
  "#009999",
  "#0099aa",
  "#0099bb",
  "#0099cc",
  "#0099dd",
  "#0099ee",
  "#00aa99",
  "#00aaaa",
  "#00aabb",
  "#00aacc",
  "#00aadd",
  "#00aaee",
  "#00aaff",
  "#00bbaa",
  "#00bbbb",
  "#00bbcc",
  "#00bbdd",
  "#00bbee",
  "#00bbff",
  "#00ccbb",
  "#00cccc",
  "#00ccdd",
  "#00ccee",
  "#00ccff",
  "#00ddcc",
  "#00dddd",
  "#00ddee",
  "#00ddff",
  "#00eedd",
  "#00eeee",
  "#00eeff",
  "#00ffee",
  "#00ffff",
  "#086666",
  "#086677",
  "#086688",
  "#086699",
  "#087777",
  "#087788",
  "#087799",
  "#0877aa",
  "#0877bb",
  "#088888",
  "#088899",
  "#0888aa",
  "#0888bb",
  "#0888cc",
  "#089988",
  "#089999",
  "#0899aa",
  "#0899bb",
  "#0899cc",
  "#0899dd",
  "#0899ee",
  "#08aa99",
  "#08aaaa",
  "#08aabb",
  "#08aacc",
  "#08aadd",
  "#08aaee",
  "#08aaff",
  "#08bbaa",
  "#08bbbb",
  "#08bbcc",
  "#08bbdd",
  "#08bbee",
  "#08bbff",
  "#08ccbb",
  "#08cccc",
  "#08ccdd",
  "#08ccee",
  "#08ccff",
  "#08ddcc",
  "#08dddd",
  "#08ddee",
  "#08ddff",
  "#08eedd",
  "#08eeee",
  "#08eeff",
  "#08ffee",
  "#08ffff",
  "#116666",
  "#116677",
  "#116688",
  "#116699",
];

const hotToneColors = [
  "#996c55",
  "#9b6c4c",
  "#9d6c42",
  "#9f6c38",
  "#a06b2d",
  "#a16b20",
  "#a26b10",
  "#a36b00",
  "#a36b00",
  "#a46b00",
  "#a16956",
  "#a2694c",
  "#a46842",
  "#a56838",
  "#a7682d",
  "#a86821",
  "#a96812",
  "#a96700",
  "#aa6700",
  "#aa6700",
  "#a76556",
  "#a9654c",
  "#ab6543",
  "#ac6439",
  "#ad642e",
  "#ae6422",
  "#af6413",
  "#b06400",
  "#b06400",
  "#b16400",
  "#ae6156",
  "#b0614d",
  "#b16043",
  "#b36039",
  "#b4602e",
  "#b56023",
  "#b66014",
  "#b66000",
  "#b76000",
  "#b75f00",
  "#b55d56",
  "#b75c4d",
  "#b85c43",
  "#b95c39",
  "#ba5c2f",
  "#bb5b23",
  "#bc5b15",
  "#bd5b00",
  "#bd5b00",
  "#bd5b00",
  "#bb5857",
  "#bd584d",
  "#be5744",
  "#c0573a",
  "#c15730",
  "#c15724",
  "#c25617",
  "#c35602",
  "#c35600",
  "#c45600",
  "#c25357",
  "#c3524e",
  "#c55244",
  "#c6523a",
  "#c75130",
  "#c85125",
  "#c85118",
  "#c95104",
  "#c95100",
  "#ca5100",
  "#c84d57",
  "#ca4c4e",
  "#cb4c45",
  "#cc4c3b",
  "#cd4b31",
  "#ce4b26",
  "#ce4b19",
  "#cf4b06",
  "#d04b00",
  "#d04a00",
  "#cf4658",
  "#d0454e",
  "#d14545",
  "#d2453b",
  "#d34431",
  "#d44427",
  "#d5441a",
  "#d54409",
  "#d64400",
  "#d64300",
  "#d53e58",
  "#d63d4f",
  "#d73d45",
  "#d83d3c",
  "#d93c32",
  "#da3c27",
  "#db3c1b",
  "#db3c0b",
  "#dc3b00",
  "#dc3b00",
];

// Utility to get a random color from a color array
function getRandomColorFromArray(colorArray) {
  return colorArray[Math.floor(Math.random() * colorArray.length)];
}

const defaultBarOptions = ["undefined", ""];

export const ChartCashFlow = ({
  part = "",
  isAverage = false,
  futurePlanData = {},
  educationData = {},
  dataDebt,
  labels,
  merge_keys = [],
  merge_keys_second = [],
  isRemoveGray = false,
  width = 500,
  height = undefined,
  heightOutside = undefined,
  outSideStyle = "overflow-auto w-full h-[350px]",
  isHasDataLabel = true,
  isHasChart = true,
  barOptions = {
    barThickness: 20,
  },
  options = {
    devicePixelRatio: 2,
    responsive: false,
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
        // barThickness: 20,
        categoryPercentage: 0.1, // Adjust spacing between bar groups
        barPercentage: 0.1, // Adjust spacing between individual bars
        ticks: {
          autoSkip: false,
          font: {
            size: 8,
          },
        },
      },
      y: {
        stacked: true,
        ticks: {
          font: {
            size: 8,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
        labels: {
          boxWidth: 14,
          boxHeight: 14,
        },
        position: "bottom",
        align: "start",
      },
    },
  },
}) => {
  const colors = useMemo(() => {
    return merge_keys?.map((key) => {
      if (key === "income_peryear") {
        return "#47A3F7";
      } else if (key === "other_income") {
        return "#47A3F7";
      } else if (key === "remain_accumulate") {
        return "#00B61D";
      } else if (key === "outcome_peryear") {
        return "#F7A647";
      } else if (key === "out_come_stable") {
        return "#ff33b2";
      } else if (key === "expenese_car_items") {
        return "#FF0000";
      } else if (key === "expenese_children_items") {
        return "#FF0000";
      }
      // else if (key === "expenese_travel_items") {
      //   return "#FF0000";
      // }
      else if (key === "expenese_collection_items") {
        return "#FF0000";
      } else if (key === "expenese_other_items") {
        return "#FF0000";
      } else if (key === "income_legacy_items") {
        return "#00BDCE";
      } else if (key === "income_business_items") {
        return "#00FFFF";
      } else if (key === "income_property_items") {
        return "#FFA500";
      } else if (key === "income_other_items") {
        return "#0000FF";
      } else if (key === "pension_peryear") {
        return "#0bdaf2";
      }
      return "#CCCCCC"; // fallback static color
    });
  }, [merge_keys]);

  const merge_label_key = {
    expenese_home_items: "บ้าน",
    expenese_car_items: "รถ",
    expenese_children_items: "ลูก",
    expenese_travel_items: "ท่องเที่ยว",
    expenese_collection_items: "ของสะสม",
    expenese_other_items: "รายจ่ายอื่นๆ",
    income_legacy_items: "รายรับจากมรดก",
    income_business_items: "รายรับจากธุรกิจ",
    income_property_items: "รายรับจากอสังหาริม",
    income_other_items: "รายรับอื่นๆ",
    income_peryear: "รายรับในอนาคต",
    other_income: "รายรับอื่น ๆ",
    // remain_accumulate: "สินทรัพย์ลงทุน",
    outcome_peryear: "รายจ่ายผันแปร",
    out_come_stable: "รายจ่ายคงที่ (ไม่รวมหนี้)",
    // out_come_stable: "รายจ่ายคงที่ (เฉพาะหนี้)",
    outcome_total: "รวมรายจ่ายต่อปี ",
    pension_peryear: "บำนาญต่อปี",
    remain: "เงินคงเหลือ",
    income_investment: "ผลตอบแทนการลงทุน",
    debt_shortterm_other: "หนี้ดอกลอย",
    debt_informal_shortterm: "หนี้นอกระบบ",
    debt_shortterm: "หนี้ระยะสั้น",
    debt_creditcard: "หนี้บัตรเครดิต",
    expenese_children_education: "การศึกษาบุตร",
    sum_expenese_total: "รวมรายจ่ายต่อปี",
  };

  const negativeList = [
    "บ้าน",
    "รถ",
    "ลูก",
    "ท่องเที่ยว",
    "รายจ่ายอื่นๆ",
    "รายจ่ายผันแปร",
    "รายจ่ายคงที่ (ไม่รวมหนี้)",
    "รายจ่ายคงที่ (เฉพาะหนี้)",
    "หนี้นอกระบบ",
    "หนี้ระยะสั้น",
    "หนี้บัตรเครดิต",
    "การศึกษาบุตร",
  ];

  const datasets = dataDebt
    ?.map((data, index) => {
      let merge_key =
        merge_label_key[merge_keys[index]] !== undefined
          ? merge_label_key[merge_keys[index]]
          : merge_keys[index];
      let tempExpenseSet = [];

      if (negativeList.includes(merge_key)) {
        tempExpenseSet = data?.map((item, index) =>
          Math.abs(item) === 0 || item === "" ? 0 : -Math.abs(item)
        );
      } else {
        tempExpenseSet = data?.map((item, index) => {
          if (merge_key === "other_income") {
            return item === -0 || item === "" ? 0 : item * 12;
          }
           return item === -0 || item === "" ? 0 : item;
        });
      }

      // console.log("merge_keys[index]", merge_keys[index]);
      // console.log("tempExpenseSet", tempExpenseSet);
      // Use color function for each dataset

      if (merge_key === "ผลตอบแทนการลงทุน") {
        return {
          label: `${merge_key}`,
          data: tempExpenseSet,
          backgroundColor: "#9900ff",
          ...barOptions,
        };
      }

      return {
        label: `${merge_key}`,
        data: tempExpenseSet,
        backgroundColor: tempExpenseSet?.some((item) => item < 0)
          ? hotToneColors[Math.floor(Math.random() * hotToneColors.length)]
          : coldToneColors[Math.floor(Math.random() * coldToneColors.length)],
        ...barOptions,
      };
    })
    ?.filter(
      (item) =>
        item?.label !== "sum_expenese_total" &&
        item?.label !== "sum_income_total" &&
        item?.label !== "remain_accumulate"
    );

  const datasets_second = merge_keys_second?.map((item, index) => {
    let merge_key = "";
    let tempExpenseSet = [];

    // รายจ่ายบ้าน
    if (item?.includes("_Is_type_expense_home_expense")) {
      merge_key = item?.split("_Is_type_expense_home_expense")[0];
      let finddata = futurePlanData?.expenese_home_items?.find((item) => {
        return item?.title === merge_key;
      });

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "ซื้อบ้าน";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year > indexA) {
          return finddata?.sum_invest_five * -1;
        }
        if (finddata?.year + finddata?.period_year > indexA) {
          return finddata?.pay_per_year * -12;
        }
        return 0;
      });
    }

    // รายจ่ายรถ
    if (item?.includes("_Is_type_expense_car_expense")) {
      merge_key = item?.split("_Is_type_expense_car_expense")[0];
      let finddata = futurePlanData?.expenese_car_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "ซื้อรถ";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year > indexA) {
          return finddata?.sum_invest_five * -1;
        }
        if (finddata?.year + finddata?.period_year > indexA) {
          return finddata?.pay_per_year * -12;
        }
        return 0;
      });
    }

    // รายจ่ายลูก
    if (item?.includes("_Is_type_expense_child_expense")) {
      merge_key = item?.split("_Is_type_expense_child_expense")[0];

      let finddata = futurePlanData?.expenese_children_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "วางแผนมีลูก";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.ishasEducation) {
          if (finddata?.year > indexA) {
            return finddata?.sum_invest_five * -1;
          }

          if (finddata?.year - 1 < indexA && finddata?.year + 19 > indexA) {
            let eduExpense = Math.ceil(
              key_under_education?.[finddata?.education_under]?.default_value *
                Math.pow(1.06, indexA - 1)
            );
            return eduExpense * -1;
          }

          return 0;
        }

        if (finddata?.year - 1 > indexA) {
          return finddata?.sum_invest_five * -1;
        }

        return 0;
      });
    }

    // รายจ่ายการศึกษาลูก
    if (item?.includes("_Is_type_expense_child_Education")) {
      merge_key = item?.split("_Is_type_expense_child_Education")[0];

      let finddata = educationData?.find((item) => item?.name === merge_key);

      tempExpenseSet = finddata?.education_items?.map((itemA, indexA) => {
        return itemA?.sum_under_education * -1;
      });
    }

    // รายจ่ายท่องเที่ยว
    if (item?.includes("_Is_type_expense_travel_expense")) {
      merge_key = item?.split("_Is_type_expense_travel_expense")[0];

      let finddata = futurePlanData?.expenese_travel_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "ท่องเที่ยว";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year > indexA) {
          return finddata?.sum_invest_five * -1;
        }

        return 0;
      });
    }

    // รายจ่ายสะสม
    if (item?.includes("_Is_type_expense_collect_expense")) {
      merge_key = item?.split("_Is_type_expense_collect_expense")[0];

      let finddata = futurePlanData?.expenese_collection_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "ซื้อของสะสม";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year > indexA) {
          return finddata?.sum_invest_five * -1;
        }

        return 0;
      });
    }

    // รายจ่ายอื่น ๆ
    if (item?.includes("_Is_type_expense_other_expense")) {
      merge_key = item?.split("_Is_type_expense_other_expense")[0];

      let finddata = futurePlanData?.expenese_other_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "รายจ่ายอื่น ๆ";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year > indexA) {
          return finddata?.sum_invest_five * -1;
        }

        return 0;
      });
    }

    // รายรับมรดก
    if (item?.includes("_Is_type_income_legacy_income")) {
      merge_key = item?.split("_Is_type_income_legacy_income")[0];

      let finddata = futurePlanData?.income_legacy_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "รับมรดก";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year === indexA) {
          return finddata?.amount;
        }

        return 0;
      });
    }

    // รายรับธุรกิจ
    if (item?.includes("_Is_type_income_business_income")) {
      merge_key = item?.split("_Is_type_income_business_income")[0];

      let finddata = futurePlanData?.income_business_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "ขายธุรกิจ";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year === indexA) {
          return finddata?.amount;
        }

        return 0;
      });
    }

    // รายรับอสังหาริมทรัพย์
    if (item?.includes("_Is_type_income_property_income")) {
      merge_key = item?.split("_Is_type_income_property_income")[0];

      let finddata = futurePlanData?.income_property_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "รายรับจากอสังหาริมทรัพย์ (ขาย เช่า เซ้ง)";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.is_rent) {
          if (finddata?.year > indexA) {
            return 0;
          }
          if (
            finddata?.year <= indexA &&
            finddata?.year + finddata?.year_rent > indexA
          ) {
            return finddata?.amount_perYear;
          }
          return 0;
        }

        return indexA === finddata?.year ? finddata?.amount : 0;
      });
    }

    // รายรับอื่นๆ
    if (item?.includes("_Is_type_income_Other_income")) {
      merge_key = item?.split("_Is_type_income_Other_income")[0];

      let finddata = futurePlanData?.income_other_items?.find(
        (item) => item?.title === merge_key
      );

      if (defaultBarOptions.includes(merge_key)) {
        merge_key = "รายรับอื่น ๆ";
      }

      tempExpenseSet = futurePlanData?.cash_flow_items?.map((itemA, indexA) => {
        if (finddata?.year > indexA) {
          return 0;
        }
        if (
          finddata?.year <= indexA &&
          finddata?.year + finddata?.year_rent > indexA
        ) {
          return finddata?.amount_perYear;
        }
        return 0;
      });
    }

    return {
      label: `${merge_key}`,
      data: tempExpenseSet,
      backgroundColor: tempExpenseSet?.some((item) => item < 0)
        ? hotToneColors[Math.floor(Math.random() * hotToneColors.length)]
        : coldToneColors[Math.floor(Math.random() * coldToneColors.length)],
      ...barOptions,
    };
  });

  const tempDataSets = [...datasets, ...datasets_second];
  // ?.map((item, index) => {
  //     return {
  //       ...item,
  //       backgroundColor: item?.data?.some((item2) => item2 < 0)
  //         ? hotToneColors[index]
  //         : coldToneColors[index],
  //     };
  //   })

  const data = {
    labels: labels,
    datasets: tempDataSets,
  };

  const config = {
    type: "bar",
    data: data,
    options: options,
  };

  const maxValue = Math.max(...datasets.flatMap((dataset) => dataset?.data));
  const dynamicHeight = Math.min(4000 + maxValue * 100, 800);
  const new_datasets = () => {
    let tempData = [...datasets, ...datasets_second];
    if (!isAverage) {
      return tempData.filter(
        (item) => !["remain_accumulate"].includes(item.label)
      );
    }
    let new_data = tempData?.map((item) => {
      const tempData = [...item?.data];
      let averageData = item?.data?.map((item, index) => {
        if (Number((futurePlanData?.age_current + index) % 5) === Number(0)) {
          let sumdata =
            Number(tempData[index] || 0) +
            Number(tempData[index - 1] || 0) +
            Number(tempData[index - 2] || 0) +
            Number(tempData[index - 3] || 0) +
            Number(tempData[index - 4] || 0);
          return sumdata / 5;
        }
        return 0;
      });
      return {
        ...item,
        data: averageData,
      };
    });
    return new_data.filter(
      (item) => !["remain_accumulate"].includes(item.label)
    );
  };

  return (
    <>
      {isHasChart && (
        <div className={outSideStyle}>
          <div
            className={cn(
              ` relative w-full h-[${
                heightOutside ? heightOutside : dynamicHeight
              }px]`,
              {
                noOutsideHeight: !heightOutside,
                heightOutside,
              }
            )}
          >
            <Bar
              width={width}
              height={height ? height : dynamicHeight}
              data={data}
              config={config}
              options={options}
            />
          </div>
        </div>
      )}
      {isHasDataLabel && (
        <div className="flex flex-wrap h-fit ">
          {new_datasets()?.map((item, index) => {
            return (
              <div
                key={index}
                className="m-[5px] flex items-center justify-center space-x-[5px] "
              >
                <div
                  style={{ backgroundColor: item?.backgroundColor }}
                  className={`w-[15px] h-[15px] `}
                ></div>
                <div>{item?.label}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
